import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, Printer } from "lucide-react";
import { api } from "../lib/api";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtDate = (s) => new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

const Row = ({ k, v, testId }) => (v ? <div className="flex justify-between gap-4 py-2 border-b border-slate-100 text-sm"><dt className="text-slate-500">{k}</dt><dd className="font-semibold text-right text-[#0F172A]" data-testid={testId}>{v}</dd></div> : null);

/** V201 — Public fee quotation (/fee-quote/:token). Standalone so Print → PDF holds only the quote. */
export default function PublicFeeQuote() {
    const { token } = useParams();
    const [sp] = useSearchParams();
    const [q, setQ] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => { api.get(`/public/fee-quote/${token}`).then((r) => setQ(r.data)).catch(() => setErr("Quotation not found or link expired.")); }, [token]);
    useEffect(() => { if (q && sp.get("print") === "1") setTimeout(() => window.print(), 600); }, [q, sp]);

    if (err) return <div className="min-h-screen grid place-items-center text-slate-600 text-sm" data-testid="fee-quote-error">{err}</div>;
    if (!q) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
    return (
        <div className="min-h-screen bg-slate-100 print:bg-white py-6 sm:py-10 px-4" data-testid="fee-quote-page">
            <Helmet><title>Fee Quotation · Anand Tutorials</title><meta name="robots" content="noindex,nofollow" /></Helmet>
            <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm print:shadow-none overflow-hidden">
                <header className="bg-[#0F172A] text-white p-5 sm:p-6 flex items-center gap-4 print:bg-[#0F172A]" style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
                    <img src="/logo-anand-tutorials.png" alt="Anand Tutorials" className="h-12 w-auto bg-white rounded-lg p-1.5" />
                    <div className="flex-1 min-w-0">
                        <div className="font-heading text-xl font-bold">Fee Quotation</div>
                        <div className="text-xs text-slate-300">1:1 Live Online Classes · {q.quote_no}</div>
                    </div>
                </header>
                <div className="p-5 sm:p-6 space-y-5">
                    <div className="text-sm text-slate-600">Issued {fmtDate(q.issued_at)} · valid until {fmtDate(q.valid_until)}</div>
                    <dl>
                        <Row k="Parent" v={q.parent_name} testId="fee-quote-parent" />
                        <Row k="Student" v={q.student_name} />
                        <Row k="City" v={`${q.city} (Tier ${q.tier})`} testId="fee-quote-city" />
                        <Row k="Course" v={q.category} testId="fee-quote-category" />
                        <Row k="Board / curriculum" v={q.board} testId="fee-quote-board" />
                        <Row k="Class / grade" v={q.grade} />
                        <Row k="Subject" v={[q.subject, q.special].filter(Boolean).join(" · ")} />
                        <Row k="Session duration" v={`${q.duration} minutes`} />
                        <Row k="Sessions per week" v={String(q.sessions_per_week)} />
                        <Row k="Sessions per month" v={`${q.monthly_sessions} (${q.monthly_detail})`} />
                        <Row k="Subjects" v={q.subjects_note} />
                        <Row k="Recommended fee range" v={`${inr(q.range.min)} – ${inr(q.range.max)} ${q.fee_basis === "monthly" ? "per month" : "per session"}`} testId="fee-quote-range" />
                    </dl>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-xl bg-[#1E3A8A] text-white p-4" style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-blue-200">{q.fee_basis === "monthly" ? "Quoted monthly fee" : "Quoted fee / session"}</div>
                            <div className="font-heading text-2xl font-bold" data-testid="fee-quote-fee">{inr(q.quoted_fee)}</div>
                        </div>
                        <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Estimated monthly</div>
                            <div className="font-heading text-2xl font-bold text-[#0F172A]" data-testid="fee-quote-monthly">{inr(q.monthly_estimate)}</div>
                        </div>
                    </div>
                    <p className="text-xs text-slate-500">The monthly amount is an estimate based on the number of sessions in the month. Fees are payable only to Anand Tutorials. Contact: +91 7000530316 · www.anandtutorials.com</p>
                    <button type="button" onClick={() => window.print()} className="print:hidden w-full h-11 rounded-lg bg-[#0F172A] text-white text-sm font-semibold inline-flex items-center justify-center gap-2" data-testid="fee-quote-print">
                        <Printer className="w-4 h-4" /> Print / Save as PDF
                    </button>
                </div>
            </div>
        </div>
    );
}
